import React, { useEffect, useState } from "react";

interface PersonaAvatarProps {
  /** Persona name — used for alt text and the initials fallback. */
  name: string;
  /** Signed GCS URL for the avatar. Absent when the persona has no image. */
  src?: string;
  /** Size, shape, and border classes for the wrapper (e.g. "w-10 h-10 rounded-full"). */
  className?: string;
  /** Extra classes applied to the <img> only (e.g. "grayscale brightness-90"). */
  imgClassName?: string;
  /**
   * Classes for the fallback initials (defaults to "text-sm"). The component
   * supplies `font-label` unless this string already names a font family, in
   * which case the caller's face is used instead — see FONT_FAMILY_CLASS.
   */
  textClassName?: string;
  /**
   * Inline styles for the wrapper. Exists for values that are only known at
   * runtime and so cannot be a class — the persona-hue border on the briefing
   * roster, which needs a color-mix against a hue resolved from cast position.
   */
  style?: React.CSSProperties;
  /**
   * Styles merged over `style` when the initials fallback renders rather than
   * an image. Only this component knows whether a load failed, so a caller that
   * wants the tile treated differently from the photo — a tinted fill, a softer
   * border — has to hand the treatment in ahead of time. Inline, so it wins over
   * the fallback's own background and text classes.
   */
  fallbackStyle?: React.CSSProperties;
  /**
   * Alt text for the image. Defaults to the persona's name. Pass `""` where
   * the name is already adjacent in the markup: the avatar is then decorative,
   * and the whole tile — image or initials — is hidden from assistive tech so
   * a screen reader lands on the name once rather than twice.
   */
  alt?: string;
}

/**
 * A persona's initials: first letter of the first word plus first letter of the
 * last, uppercased. Exported so surfaces that draw their own portrait block
 * (rather than reusing this component's fixed square) fall back to the same
 * initials rather than inventing a second rule.
 */
export const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
};

/**
 * The font-family utilities this theme defines (see the Tailwind config in
 * web/index.html). Tailwind emits utilities in config order rather than source
 * order, so a caller's `font-headline` does not reliably beat a hard-coded
 * `font-label` on the same element — it just collides with it. Detecting the
 * caller's face and standing down is the only way to let it win.
 */
const FONT_FAMILY_CLASS = /(?:^|\s)font-(?:headline|body|label|sans|serif|mono)(?:\s|$)/;

/**
 * Persona avatar with a built-in fallback: when no image is set, or the image
 * fails to load (e.g. a signed URL expired while the page sat open), renders the
 * persona's initials in a neutral box instead of the browser's broken-image icon.
 */
const PersonaAvatar: React.FC<PersonaAvatarProps> = ({
  name,
  src,
  className = "",
  imgClassName = "",
  textClassName = "text-sm",
  style,
  fallbackStyle,
  alt,
}) => {
  const [failed, setFailed] = useState(false);

  // A fresh src (e.g. a newly signed URL) deserves another attempt.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showImage = !!src && !failed;
  // An explicit empty alt is the caller saying the name is already in the
  // markup beside this, so neither the image nor the initials fallback should
  // be announced.
  const decorative = alt === "";
  const labelled = !showImage && !decorative;

  return (
    <div
      className={`overflow-hidden flex items-center justify-center select-none flex-shrink-0 ${
        showImage ? "" : "bg-surface-container-highest text-on-surface/70"
      } ${className}`}
      style={showImage ? style : { ...style, ...fallbackStyle }}
      role={labelled ? "img" : undefined}
      aria-label={labelled ? name : undefined}
      title={labelled ? name : undefined}
      aria-hidden={decorative || undefined}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name}
          onError={() => setFailed(true)}
          className={`w-full h-full object-cover ${imgClassName}`}
        />
      ) : (
        <span
          className={`${
            FONT_FAMILY_CLASS.test(textClassName) ? "" : "font-label"
          } font-bold uppercase leading-none ${textClassName}`}
        >
          {initialsOf(name)}
        </span>
      )}
    </div>
  );
};

export default PersonaAvatar;
